import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button, Menu } from 'antd'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  AlertOutlined,
  BellOutlined,
  DesktopOutlined,
  EyeOutlined,
  HomeOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  SendOutlined,
  UnorderedListOutlined,
  VideoCameraOutlined
} from '@ant-design/icons'
import menusConfig, { MATERNITY_GROUP_KEY } from '@/components/AppHeader/menus'
import { mergeClientPermissionCodeLists } from '@/constants/menuPermissionMeta'
import { isMenuItemVisible, stripMenuRBACFields } from '@/utils/menuItemVisible'

/** 监控墙侧栏：无人操作后收成窄条 */
const SURVEILLANCE_SIDER_IDLE_MS = 10 * 60 * 1000

const SIDER_ICON_BY_PATH = {
  '/client/visualScreen': DesktopOutlined,
  '/client/commandCenter': HomeOutlined,
  '/client/order/orderList': UnorderedListOutlined,
  '/client/order/dispatch': SendOutlined,
  '/client/order/serviceNotification': BellOutlined,
  '/client/ys7/devices': VideoCameraOutlined,
  '/client/ys7/log': AlertOutlined,
  '/client/surveillance/list': EyeOutlined
}

function siderSelectedKeys (pathname, items) {
  const keys = (items || [])
    .map((item) => item && item.key)
    .filter((key) => typeof key === 'string' && key.startsWith('/'))
  const matched = keys.filter((key) => pathname === key || pathname.startsWith(`${key}/`))
  if (matched.length === 0) return []
  matched.sort((a, b) => b.length - a.length)
  return [matched[0]]
}

/** 月子 / 按摩左侧栏：子项与顶栏对应组同源。监控墙可收成图标窄条 */
export default function ClientMaternitySider ({ groupKey, collapsible }) {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { permissionCodes, permissionCodesLoaded } = useSelector((state) => state.app)
  const mergedRbacCodes = useMemo(
    () => mergeClientPermissionCodeLists(permissionCodes),
    [permissionCodes]
  )
  const siderGroupKey = groupKey || MATERNITY_GROUP_KEY
  const canCollapse = !!collapsible
  const [collapsed, setCollapsed] = useState(false)
  const idleTimerRef = useRef(null)

  useEffect(() => {
    if (!canCollapse) setCollapsed(false)
  }, [canCollapse])

  useEffect(() => {
    if (!canCollapse || collapsed) return undefined
    const bump = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current)
      idleTimerRef.current = setTimeout(() => {
        setCollapsed(true)
        idleTimerRef.current = null
      }, SURVEILLANCE_SIDER_IDLE_MS)
    }
    bump()
    const opts = { passive: true }
    window.addEventListener('pointermove', bump, opts)
    window.addEventListener('pointerdown', bump, opts)
    window.addEventListener('keydown', bump)
    window.addEventListener('wheel', bump, opts)
    return () => {
      window.removeEventListener('pointermove', bump, opts)
      window.removeEventListener('pointerdown', bump, opts)
      window.removeEventListener('keydown', bump)
      window.removeEventListener('wheel', bump, opts)
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current)
        idleTimerRef.current = null
      }
    }
  }, [canCollapse, collapsed])

  const items = useMemo(() => {
    const group = menusConfig.find((item) => item && item.key === siderGroupKey)
    const children = group && Array.isArray(group.children) ? group.children : []
    return children
      .filter((child) => isMenuItemVisible(child, mergedRbacCodes, permissionCodesLoaded))
      .map((child) => {
        const item = stripMenuRBACFields(child)
        if (!item) return null
        if (!canCollapse) return item
        const Icon = SIDER_ICON_BY_PATH[item.key]
        return Icon ? { ...item, icon: <Icon /> } : item
      })
      .filter(Boolean)
  }, [canCollapse, mergedRbacCodes, permissionCodesLoaded, siderGroupKey])

  const onClick = useCallback(
    (info) => {
      if (canCollapse && collapsed) {
        setCollapsed(false)
        return
      }
      const key = info && info.key
      if (typeof key === 'string' && key.startsWith('/')) {
        navigate(key)
      }
    },
    [canCollapse, collapsed, navigate]
  )

  const onSiderClick = useCallback(() => {
    if (canCollapse && collapsed) setCollapsed(false)
  }, [canCollapse, collapsed])

  const onToggle = useCallback((e) => {
    e.stopPropagation()
    setCollapsed((prev) => !prev)
  }, [])

  if (items.length === 0) return null

  const siderClassName = [
    'client-app__sider',
    canCollapse ? 'client-app__sider--collapsible' : '',
    canCollapse && collapsed ? 'client-app__sider--collapsed' : ''
  ].filter(Boolean).join(' ')

  return (
    <aside
      className={siderClassName}
      onClick={onSiderClick}
      title={canCollapse && collapsed ? '点击展开导航' : undefined}
    >
      <Menu
        mode="inline"
        inlineCollapsed={canCollapse && collapsed}
        selectedKeys={siderSelectedKeys(pathname, items)}
        onClick={onClick}
        items={items}
      />
      {canCollapse ? (
        <Button
          type="text"
          className="client-app__sider-toggle"
          icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
          onClick={onToggle}
          aria-label={collapsed ? '展开导航' : '收起导航'}
        >
          {collapsed ? null : '收起'}
        </Button>
      ) : null}
    </aside>
  )
}
